import { type ComponentPropsWithoutRef, type Ref, useEffect, useRef } from 'react';

import { cn } from '~/utils/cn';

type LabelProps = Omit<ComponentPropsWithoutRef<'label'>, 'onChange' | 'children'>;

interface SelectionCheckboxProps extends LabelProps {
  /** Forwarded to the label. Radix's `asChild` triggers need the DOM node, so
   *  a dropped ref here silently breaks tooltip positioning. */
  ref?: Ref<HTMLLabelElement>;
  checked: boolean;
  /** Partial selection — renders the bar instead of the tick. Applied to the
   *  input as a DOM property, since `indeterminate` has no HTML attribute. */
  indeterminate?: boolean;
  disabled?: boolean;
  /** Accessible name. Should say what a click will do, not just "select". */
  label: string;
  onCheckedChange: (next: boolean) => void;
  /** Adds `data-scoped-selected` to the label so a row can tint itself in CSS
   *  off the selection, without the selection entering the row's render path. */
  markSelected?: boolean;
  /** Automation hook (default `selection-checkbox`) on the label; the input gets `${testId}-input`. */
  testId?: string;
}

/**
 * Row-selection checkbox for the product tables and for `DataTable`'s injected
 * selection column (`enableRowSelection`).
 *
 * A real `<input type="checkbox">` inside a `<label>` that fills its cell: the
 * label is the click target (so a near-miss still ticks the box rather than
 * activating the row behind it), the input stays focusable and keyboard-
 * operable, and the painted box is a sibling driven purely by
 * `input:checked` / `:indeterminate` / `:focus-visible` in CSS.
 *
 * Styling lives in `styles/tailwind/selection-checkbox.css` — it needs real
 * sibling and `:has()` selectors that would be unreadable as stacked utilities.
 */
export function SelectionCheckbox({
  ref,
  checked,
  indeterminate = false,
  disabled = false,
  label,
  onCheckedChange,
  markSelected = false,
  testId = 'selection-checkbox',
  className,
  ...labelProps
}: SelectionCheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // `indeterminate` is a property, not an attribute — React won't set it for us.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      ref={ref}
      className={cn('selection-checkbox', className)}
      data-testid={testId}
      {...(markSelected ? { 'data-scoped-selected': '' } : {})}
      {...labelProps}
    >
      <input
        ref={inputRef}
        type="checkbox"
        className="selection-checkbox__input"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        data-testid={`${testId}-input`}
        onChange={(e) => {
          onCheckedChange(e.target.checked);
        }}
      />
      <span aria-hidden="true" className="selection-checkbox__box">
        <svg
          className="selection-checkbox__check"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 6.4 4.6 9 10 3.2" />
        </svg>
        <span className="selection-checkbox__bar" />
      </span>
    </label>
  );
}
